import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  GetGroupsDocument,
  UpdateGroupDocument,
  type UpdateGroupMutation,
  type UpdateGroupMutationVariables,
} from "~/features/admin/groups/gqls";
import type { GroupEditFormData } from "~/features/admin/groups/types";
import { translateError } from "~/lib/errors";

interface UseGroupEditOptions {
  onSuccess?: (data: GroupEditFormData) => void;
}

export function useGroupEdit({ onSuccess }: UseGroupEditOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [updateGroup] = useMutation<UpdateGroupMutation, UpdateGroupMutationVariables>(UpdateGroupDocument, {
    refetchQueries: [GetGroupsDocument],
  });

  const submit = useCallback(
    async (formData: GroupEditFormData) => {
      setProcessing(true);
      try {
        await updateGroup({
          variables: {
            input: {
              id: formData.id,
              name: formData.name,
              description: formData.description,
            },
          },
        });
        toast.success(t("feature.common.updatedSuccessfully", { model: "group" }));
        onSuccess?.(formData);
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [updateGroup, t, onSuccess],
  );

  return { submit, processing };
}
